import PropTypes from 'prop-types';
import styled from 'styled-components';
import { IconButton, Menu, MenuItem, MenuSection } from '@workramp/ui';
import { DropdownV2, DefaultDropdownMenu, DefaultDropdownSingleSelect } from '../widget/dropdown-2';
import SortIcon from '../icons/v2/sort';
import CaretIcon from '../icons/v2/caret';
import OverflowIcon from '../icons/v2/overflow';
import TruncatedDiv from '../widget/truncated-div';
import { ReactVirtualizedTableWithCalculatedHeight } from './react-virtualized-table';
import SkeletonPulse, { DefaultSkeletonWrapper } from './v2/skeleton-loader';

const PAGINATION_HEIGHT = 56;

const { I18n } = window;

const ActionDropdown = ({ actions, destructiveActions, data, disabled, newUi, ...props }) =>
  newUi ? (
    <Menu
      placement="bottom-end"
      onAction={(action) =>
        [...actions, ...destructiveActions]
          .find(({ displayText }) => {
            const menuItemText = typeof displayText === 'string' ? displayText : displayText(data);
            return menuItemText === action;
          })
          .onClick(data)
      }
      buttonEl={
        <IconButton
          size="md"
          label="More Actions"
          variant="text"
          color="secondary"
          iconName="overflow-vertical:regular"
          tooltip={{
            message: 'More Actions',
            strategy: 'fixed',
            placement: 'bottom-end',
          }}
        />
      }
    >
      {actions.length > 0 && (
        <MenuSection aria-label="Options">
          {actions.map(({ displayText, color }) => {
            const menuItemText = typeof displayText === 'string' ? displayText : displayText(data);
            return (
              <MenuItem key={menuItemText} color={color}>
                {menuItemText}
              </MenuItem>
            );
          })}
        </MenuSection>
      )}
      {destructiveActions.length > 0 && (
        <MenuSection aria-label="Delete options">
          {destructiveActions.map(({ displayText, color }) => {
            const menuItemText = typeof displayText === 'string' ? displayText : displayText(data);
            return (
              <MenuItem key={menuItemText} color={color}>
                {menuItemText}
              </MenuItem>
            );
          })}
        </MenuSection>
      )}
    </Menu>
  ) : (
    <DropdownV2
      button={
        <EditIconWrapper>
          <OverflowIcon horizontal={false} />
        </EditIconWrapper>
      }
    >
      <StyledDefaultDropdownMenu width={140} direction="SE" offsets={{ right: 16 }} {...props}>
        <DefaultDropdownSingleSelect
          options={actions.map(({ displayText, onClick }) => ({
            disabled,
            displayText: typeof displayText === 'string' ? displayText : displayText(data),
            onClick: () => onClick(data),
          }))}
        />
      </StyledDefaultDropdownMenu>
    </DropdownV2>
  );

/** @deprecated prefer `import { Table } from '@workramp/ui';` */
function CommonTable({
  isLoading = false,
  hasPagination = false,
  hasSelection = false,
  selectedRows = [],
  onRowsSelect,
  columns,
  visibleRows,
  pageSize = 15,
  pageIndex = 0,
  totalRows = 0,
  onPageChange,
  rowPadding = 0,
  showRowBorders = true,
  customTableHeight,
  emptyTableHeight,
  rowActions = [],
  destructiveActions = [],
  bulkActions = [],
  noRowsRenderer,
  headerHeight = 48,
  allowOverflow = true,
  rowHeight = 48,
  padLeftmostColumn = false,
  newUi = false,
  ...props
}) {
  const isEmptyTable = visibleRows.length === 0;

  // Change header column styling
  const mappedCols = columns.map((col) => ({
    headerRenderer: ({ label, dataKey, sortBy, sortDirection, disableSort }) => {
      if (disableSort) {
        return <ColumnTitle>{label}</ColumnTitle>;
      }

      // Column is currently sorted
      if (sortBy === dataKey) {
        return (
          <ColumnTitle>
            <span>{label}</span>
            <SortIndicator order={sortDirection.toLowerCase()} color="currentColor" />
          </ColumnTitle>
        );
      }

      // Unsorted column
      return (
        <ColumnTitle>
          <span>{label}</span>
          <SortIndicator order="default" color="currentColor" />
        </ColumnTitle>
      );
    },
    cellRenderer: ({ cellData }) => <TruncatedDiv text={cellData} />,
    ...col,
  }));

  // Slice out the visible rows for current page
  const startIndex = pageIndex * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalRows);

  // Add checkbox column for selections
  const selectCol = {
    label: '',
    dataKey: '',
    disableSort: true,
    width: 16,
    maxWidth: 16,
    headerRenderer: () => {
      // Don't show a checked state for an empty table
      const hasData = visibleRows.length > 0;

      // Mark checked if all visible rows have been selected
      const selectedRowIds = selectedRows.map(({ id }) => id);
      const isChecked = hasData && visibleRows.every((row) => selectedRowIds.includes(row.id));

      // Only mark visible rows as selected
      // Filter out any rows that have already been selected
      const bulkSelectedRows = isChecked ? visibleRows : visibleRows.filter(({ id }) => !selectedRowIds.includes(id));
      return (
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => onRowsSelect(bulkSelectedRows, !isChecked)}
          disabled={!hasData}
        />
      );
    },
    cellRenderer: ({ rowData }) => (
      <input
        type="checkbox"
        checked={selectedRows.map(({ id }) => id).includes(rowData.id)}
        onChange={(event) => onRowsSelect([rowData], event.target.checked)}
      />
    ),
  };

  // Add dropdown column for actions
  const actionCol = {
    label: '',
    dataKey: '',
    disableSort: true,
    width: 32,
    maxWidth: 32,
    headerRenderer: () =>
      bulkActions.length > 0 &&
      selectedRows.length > 0 && (
        <ActionDropdown actions={bulkActions} data={selectedRows} width={200} disabled={selectedRows.length === 0} />
      ),
    cellRenderer: ({ rowData }) => (
      <RowActionDropdown newUi={newUi} actions={rowActions} destructiveActions={destructiveActions} data={rowData} />
    ),
  };

  // Add selection column if enabled
  let adjustedCols = mappedCols;
  if (hasSelection) {
    adjustedCols = [selectCol, ...adjustedCols];
  }

  // Add action column if passed
  if (rowActions.length > 0) {
    adjustedCols = [...adjustedCols, actionCol];
  }

  let adjustedCustomTableHeight = customTableHeight;

  // Provide a fixed height for empty tables
  if (isEmptyTable) {
    adjustedCustomTableHeight = emptyTableHeight ?? rowHeight * 5;
  }

  // Adjust table height to support pagination bar
  if (hasPagination && adjustedCustomTableHeight) {
    adjustedCustomTableHeight -= PAGINATION_HEIGHT;
  }

  return (
    <Wrapper rowPadding={rowPadding} showRowBorders={showRowBorders} padLeftmostColumn={padLeftmostColumn}>
      <ReactVirtualizedTableWithCalculatedHeight
        allowOverflow={allowOverflow}
        dynamicColumnWidths
        allColumnProps={adjustedCols}
        unsortedRows={visibleRows}
        headerHeight={headerHeight}
        rowHeight={rowHeight}
        customTableHeight={adjustedCustomTableHeight}
        forceCustomTableHeight={isEmptyTable}
        noRowsRenderer={() =>
          isLoading ? (
            <StyledSkeletonPulse
              count={5}
              wrapper={() => <StyledSkeleton height={adjustedCustomTableHeight - headerHeight} rowHeight={rowHeight} />}
            />
          ) : (
            noRowsRenderer()
          )
        }
        {...props}
      />

      {hasPagination && (
        <Pagination rowPadding={rowPadding}>
          <PaginationStats>
            {!isEmptyTable && (
              <span>
                {I18n.t('js.learner.pagination.showing', { range: `${startIndex + 1}-${endIndex}`, total: totalRows })}
              </span>
            )}
            {isEmptyTable && <span>Showing 0 of 0</span>}
          </PaginationStats>

          <ButtonGroup>
            <PageButton
              type="button"
              onClick={() => onPageChange(pageIndex - 1)}
              disabled={startIndex === 0}
              aria-label={I18n.t('js.learner.company.home.action_button_labels.previous_button')}
            >
              <CaretIcon left />
            </PageButton>
            <PageButton
              type="button"
              onClick={() => onPageChange(pageIndex + 1)}
              disabled={endIndex >= totalRows}
              aria-label={I18n.t('js.learner.company.home.action_button_labels.next_button')}
            >
              <CaretIcon right />
            </PageButton>
          </ButtonGroup>
        </Pagination>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  .t18-table {
    box-shadow: none;
    background-color: transparent;
    z-index: 1;
  }

  .ReactVirtualized__Table__Grid {
    background-color: transparent;
    box-shadow: none;
  }

  .ReactVirtualized__Table__headerRow {
    box-shadow: none;
    border-bottom: 1px solid #dee1e5;
    overflow: visible !important;
  }

  .ReactVirtualized__Table__headerRow,
  .ReactVirtualized__Table__row {
    background-color: transparent;
    box-sizing: border-box;
    padding-left: ${({ rowPadding }) => `${rowPadding}px`};
    padding-right: ${({ rowPadding }) => `${rowPadding}px`};
  }

  .ReactVirtualized__Table__row {
    border-bottom: none;
    max-width: 100%;
  }

  ${({ padLeftmostColumn }) =>
    !padLeftmostColumn &&
    `
    .ReactVirtualized__Table__headerColumn:first-child,
    .ReactVirtualized__Table__rowColumn:first-child {
      margin-left: 0;
    }
    `}

  ${({ showRowBorders }) =>
    showRowBorders &&
    `
    .ReactVirtualized__Grid__innerScrollContainer > * + * > .ReactVirtualized__Table__row {
      border-top: 1px solid #dee1e5;
    }
  `}

  .ReactVirtualized__Table__row:hover {
    background-color: transparent;
  }

  .ReactVirtualized__Table__headerColumn,
  .ReactVirtualized__Table__rowColumn {
    font-size: 14px;
    margin-left: 16px;
    margin-right: 0;
  }

  .ReactVirtualized__Table__rowColumn {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ReactVirtualized__Table__headerColumn {
    color: #888d95;
  }
`;

const ColumnTitle = styled.div`
  display: flex;
  align-items: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const SortIndicator = styled(SortIcon)`
  margin-left: 8px;
`;

const Pagination = styled.div`
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${PAGINATION_HEIGHT}px;
  border-top: 1px solid #dee1e5;
  padding-left: ${({ rowPadding }) => `${rowPadding}px`};
  padding-right: ${({ rowPadding }) => `${rowPadding}px`};
`;

const PaginationStats = styled.span`
  color: #000000;
  opacity: 0.5;
`;

const ButtonGroup = styled.div`
  display: flex;
`;

const PageButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  border: none;
  background-color: transparent;

  &:disabled {
    opacity: 0.25;
  }

  & + & {
    margin-left: 8px;
  }
`;

const EditIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    cursor: pointer;
  }
`;

const StyledDefaultDropdownMenu = styled(DefaultDropdownMenu)`
  padding-top: 0px;
  padding-bottom: 0px;
  overflow: hidden;
  font-weight: normal;

  div {
    margin-bottom: 0;
  }
`;

const StyledSkeletonPulse = styled(SkeletonPulse)`
  font-size: 0;
`;

const StyledSkeleton = styled(DefaultSkeletonWrapper)`
  margin-top: 3px;
  margin-bottom: 3px;
  height: calc(${({ height }) => height}px / 5 - 6px);
  max-height: ${({ rowHeight }) => rowHeight}px;
  background-color: rgba(0, 0, 0, 0.067);
  background-image: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.039), transparent);
`;

const RowActionDropdown = styled(ActionDropdown)`
  position: fixed;
  top: auto;
`;

CommonTable.propTypes = {
  allowOverflow: PropTypes.bool,
  isLoading: PropTypes.bool,
  hasSelection: PropTypes.bool,
  hasPagination: PropTypes.bool,
  showRowBorders: PropTypes.bool,
  padLeftmostColumn: PropTypes.bool,
  selectedRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ),
  onRowsSelect: PropTypes.func,
  pageSize: PropTypes.number,
  pageIndex: PropTypes.number,
  totalRows: PropTypes.number,
  onPageChange: PropTypes.func,
  rowPadding: PropTypes.number,
  customTableHeight: PropTypes.number,
  emptyTableHeight: PropTypes.number,
  noRowsRenderer: PropTypes.func,
  headerHeight: PropTypes.number,
  rowHeight: PropTypes.number,
  newUi: PropTypes.bool,
  rowActions: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  destructiveActions: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  bulkActions: PropTypes.arrayOf(
    PropTypes.shape({
      displayText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
      onClick: PropTypes.func.isRequired,
    }),
  ),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      dataKey: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  visibleRows: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }),
  ).isRequired,
};

export default CommonTable;
